import { SetStateAction } from "react";
import { TFunction } from "react-i18next";

export function validateEmail(email: string): boolean {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
}

export function validatePassword(password: string): boolean {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
}

export function validateFullName(fullName: string): boolean {
    // Only allow Latin characters, spaces, hyphens, and apostrophes
    const nameRegex = /^[A-Za-z\s'-]+$/;
    return nameRegex.test(fullName.trim()) && fullName.trim().length >= 2;
}

export function validateLatinCharacters(text: string): boolean {
    // Check if text contains only Latin characters
    const latinRegex = /^[A-Za-z\s'-\.]+$/;
    return latinRegex.test(text);
}

export function validateSSN(ssn: string): boolean {
    if (ssn && ssn.length === 12) {
        const last4 = ssn.slice(-4);
        return last4 !== "0000";
    }
    return true;
}

export function handleSSNInput(value: string): string {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
    const maxLength = 12;
    return cleaned.length > maxLength ? cleaned.slice(0, maxLength) : cleaned;
}

export function handleDOBInput(value: string): string {
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.length > 8) {
        cleaned = cleaned.substring(0, 8) + '-' + cleaned.substring(8, 10);
    }
    return cleaned.length <= 11 ? cleaned : cleaned.slice(0, 11);
}

// Signin validation helpers
export function validateEmailForSignin(email: string, t: (key: string) => string): string {
    if (!email) {
        return t("Email is required");
    }
    if (!validateEmail(email)) {
        return t("Invalid email address");
    }
    return '';
}

export function validatePasswordForSignin(password: string, t: (key: string) => string): string {
    if (!password) {
        return t("Password is required");
    }
    return '';
}

export function validateSigninForm(formData: { email: string; password: string }, t: (key: string) => string) {
    const emailError = validateEmailForSignin(formData.email, t);
    const passwordError = validatePasswordForSignin(formData.password, t);

    return {
        errors: {
            email: emailError,
            password: passwordError
        },
        isValid: !emailError && !passwordError
    };
}

export function handleSocialLoginAction(key: string, setShowBankIDModal: (show: boolean) => void) {
    switch (key) {
        case "bankid":
            setShowBankIDModal(true);
            break;
        default:
            break;
    }
}


interface FormErrors {
    [key: string]: string;
}
export const validateForm = (formData: { fullName: string; email: string; password: string; noIdNumber: any; socialSecurityNumber: string | any[]; dateOfBirth: string | any[]; agreeToTerms: any; }, t: (arg0: string) => string, setErrors: (errors: FormErrors) => void): boolean => {
    const newErrors: FormErrors = {};

    // Full name validation
    if (!formData.fullName.trim()) {
        newErrors.fullName = t("Full name is required") as string;
    } else if (!validateLatinCharacters(formData.fullName)) {
        newErrors.fullName = t("Full name must contain only Latin characters (A-Z, a-z)") as string;
    } else if (!validateFullName(formData.fullName)) {
        newErrors.fullName = t("Full name must be at least 2 characters long and contain only letters, spaces, hyphens, and apostrophes") as string;
    }

    // Email validation
    if (!formData.email.trim()) {
        newErrors.email = t("Email is required") as string;
    } else if (!validateEmail(formData.email)) {
        newErrors.email = t("Invalid email address") as string;
    }

    // Password validation
    if (!formData.password) {
        newErrors.password = t("Password is required") as string;
    } else if (!validatePassword(formData.password)) {
        newErrors.password = t("Password must contain at least one capital letter and one number. Special characters like hyphens (-), accented letters (ö, ä, å, é, ñ, etc.), and symbols (€, £, ©, ®, etc.) are not accepted.") as string;
    }

    if (!formData.noIdNumber && (!formData.socialSecurityNumber || formData.socialSecurityNumber.length < 12)) {
        newErrors.socialSecurityNumber = t("SSN number must have 12 digits.") as string;
    }

    if (!formData.noIdNumber && typeof formData.socialSecurityNumber === 'string' && !validateSSN(formData.socialSecurityNumber)) {
        newErrors.socialSecurityNumber = t("The last 4 digits of SSN cannot be all zeros.") as string;
    }

    if (formData.noIdNumber && (!formData.dateOfBirth || formData.dateOfBirth.length < 11)) {
        newErrors.dateOfBirth = t("DOB must have 11 digits.") as string;
    }


    if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = t("Please select the terms and condition") as string;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};


export const handleFormValidation = (formData: { email: string; password: string; rememberMe?: boolean; }, t: TFunction<"translation", undefined>, setErrors: { (value: SetStateAction<{ email: string; password: string; }>): void; (arg0: { email: string; password: string; }): void; }) => {
    const { errors: validationErrors, isValid } = validateSigninForm(formData, t);
    setErrors(validationErrors);
    return isValid;
};